import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { RowDataBoundEventArgs, RowSelectingEventArgs } from '@syncfusion/ej2-react-grids';
import { TreeGridComponent } from '@syncfusion/ej2-react-treegrid';
import { BeforeCloseEventArgs } from '@syncfusion/ej2-react-popups';
import FacilityStore from '@facility/stores/FacilityStore';
import * as Inovx from '@shared';
import ConfigurationActions from '@facility/actions/ConfigurationActions';
import ConfigurationConstants from '@facility/constants/ConfigurationConstants';
import SchemaActions from '@facility/actions/SchemaActions';
import SchemaServices from '@facility/services/SchemaServices';
import {
    IConfigurationTreeNode,
    IPendingTreeSelection,
    ISelectSchema,
    ITreeContextMenuState,
    SchemaFormError
} from '../types';
import {
    buildConfigurationTreeNode,
    collectConfigurationTreeNodeKeys,
    findTreeNodeByKey,
    fontSizeOptions,
    getAttributeDataTypeLabels,
    getConfigurationTreeNodeKey,
    getDeletingProgressMessage,
    getSavingProgressMessage,
    getFontStyleOptions,
    getTagShapeOptions,
    sortByName
} from '../utils/configurationHelpers';
import { hasInvalidExternalAttributes } from '../externalAttributes/externalAttributeValidation';

var SystemStore = Inovx.Shared.System.Store;
var SystemActions = Inovx.Shared.System.Actions;
var PermissionService = Inovx.Shared.Permission.PermissionService;

const MANAGE_FACILITY_PERMISSION = 'Facility.Manage';

declare var InovxLoader: any;

function getIsActionInProgress(): boolean {
    return SystemStore.isActionInProgress(ConfigurationConstants.CONFIGURATION_EDIT);
}

function cloneAssetClasses(assetClasses: IAssetClassUpdateDetail[] | null | undefined): IAssetClassUpdateDetail[] {
    return [...(assetClasses || [])];
}

function areTreeAssetClassesEqual(left: IAssetClassUpdateDetail[] | null | undefined, right: IAssetClassUpdateDetail[] | null | undefined): boolean {
    if (left === right) {
        return true;
    }

    const leftItems = left || [];
    const rightItems = right || [];

    if (leftItems.length !== rightItems.length) {
        return false;
    }

    return leftItems.every((item, index) => {
        const compareItem = rightItems[index];
        return !!compareItem
            && item.ID === compareItem.ID
            && item.Name === compareItem.Name
            && item.SchemaID === compareItem.SchemaID;
    });
}

function isAssetClassNodeType(nodeType: IConfigurationTreeNode['NodeType'] | null | undefined): boolean {
    return nodeType === 'assetClass' || nodeType === 'assetClassExtension';
}

function isAnnotationClassNodeType(nodeType: IConfigurationTreeNode['NodeType'] | null | undefined): boolean {
    return nodeType === 'annotationClass';
}

function isItemClassNodeType(nodeType: IConfigurationTreeNode['NodeType'] | null | undefined): boolean {
    return isAssetClassNodeType(nodeType) || isAnnotationClassNodeType(nodeType);
}

function getParentSchemaIdFromTreeNodeKey(treeNodeKey: string | null | undefined): string | null {
    const match = treeNodeKey?.match(/-parent-(.+)$/);
    return match?.[1] || null;
}

function getItemClassNodeTypeFromTreeNodeKey(treeNodeKey: string | null | undefined): 'assetClass' | 'annotationClass' {
    return treeNodeKey?.startsWith('annotation-class-') ? 'annotationClass' : 'assetClass';
}

function getTreeNodeAssetClasses(node: IConfigurationTreeNode | null | undefined): Array<Pick<IAssetClassUpdateDetail, 'ID' | 'Name' | 'PrimaryAssetClassID'>> {
    return (node?.Children || [])
        .filter((childNode) => isAssetClassNodeType(childNode.NodeType))
        .map((childNode) => childNode.AssetClass || {
            ID: childNode.AssetClassID || childNode.ID,
            Name: childNode.Name,
            PrimaryAssetClassID: childNode.PrimaryAssetClassID
        });
}

function isNewAttributeID(id: string): boolean {
    return id.startsWith('temp_');
}

export interface UseConfigurationFormResult {
    isActionInProgress: boolean;
    actionInProgressMsg: string;
    hasSchemaChanges: boolean;
    canSaveConfiguration: boolean;
    canDeleteSchema: boolean;
    configuration: IAssetClassUpdateDetail | null;
    selectedTreeNode: IConfigurationTreeNode | null;
    treeDataSource: IConfigurationTreeNode[];
    treeGridRenderKey: number;
    treeGridRef: RefObject<TreeGridComponent | null>;
    treeContextMenu: ITreeContextMenuState;
    schemaUsageList: Array<{ text: string; id: string }>;
    schemaExtensionParentNames: string[];
    schemaDetailsSectionProps: {
        editableSchemaName: string;
        hasSchemaNameError: boolean;
        onSchemaNameChange: (event: ChangeEvent<HTMLInputElement>) => void;
        assetSetTaxonomyDetail: ILibraryTaxonomyDetail | null;
        onAssetSetTaxonomyChange: (taxonomy: ILibraryTaxonomyDetail) => void;
        assetSetTaxonomyResetToken: number;
    };
    assetClassDetailsSectionProps: {
        editableAssetClassName: string;
        hasAssetClassNameError: boolean;
        onAssetClassNameChange: (event: ChangeEvent<HTMLInputElement>) => void;
    };
    selectedAssetClassTab: number;
    saveConfiguration: () => Promise<void>;
    deleteSchema: () => void;
    cancel: () => void;
    onAddSchema: () => void;
    setSelectedAssetClassTab: (index: number) => void;
    handleTreeRowSelecting: (event: RowSelectingEventArgs) => void;
    handleTreeRowSelected: (event: any) => void;
    handleTreeRowDataBound: (args: RowDataBoundEventArgs) => void;
    handleTreeRowExpanded: (event: any) => void;
    handleTreeRowCollapsed: (event: any) => void;
    hideTreeContextMenu: () => void;
    openTreeContextMenu: (clientX: number, clientY: number, node: IConfigurationTreeNode) => void;
    getTreeContextMenuItems: (node: IConfigurationTreeNode) => Array<{ id: string; label: string }>;
    handleTreeContextMenuItemClick: (itemId: string) => Promise<void>;
    formFieldsSectionProps: {
        sortedAttributeDefinitions: IAttributeDefinition[];
        attributeDataTypeLabels: { [key: number]: string };
        onAddAttribute: () => void;
        onEditAttribute: (id: string) => void;
        onDeleteAttribute: (id: string) => void;
    };
    externalAttributesSectionProps: {
        assetClassID: string;
        links: IExternalDataSourceLink[];
        definitions: IExternalAttributeDefinition[];
        matchTargetAttributes: IAttributeDefinition[];
        facilities: IFacility[];
        onLinksChange: (links: IExternalDataSourceLink[]) => void;
        onDefinitionsChange: (definitions: IExternalAttributeDefinition[]) => void;
    };
    validationSettingsSectionProps: {
        configuration: IAssetClassUpdateDetail;
        tagShapeOptions: Array<{ id: string; name: string }>;
        fontSizeOptions: number[];
        fontStyleOptions: Array<{ name: string; value: string }>;
        onSelectChanged: (event: any) => void;
        onChangeColor: (name: string, color: string) => void;
    };
    importMappingSectionProps: {
        configurationName: string;
        sortedFriendClasses: IFriendClass[];
        onAddFriendClass: () => void;
        onEditFriendClass: (name: string) => void;
        onDeleteFriendClass: (name: string) => void;
        assetSetTaxonomyDetail?: ILibraryTaxonomyDetail | null;
        autoPopulateAssetSets?: boolean;
        selectedAssetSetClassificationTags?: string[];
        hasAutoPopulateClassificationError?: boolean;
        onAutoPopulateAssetSetsChange: (enabled: boolean) => void;
        onAssetSetClassificationTagsChange: (selectedTags: string[]) => void;
    };
    dialogsProps: {
        attributeDialog: {
            isOpen: boolean;
            editAttribute: IAttributeDefinition | null;
            hasAddAttributeError: boolean;
            newAttributeName: string;
            newAttributeDataType: number;
            attributeDataTypeLabels: { [key: number]: string };
            isNewAttributeId: (id: string) => boolean;
            onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
            onDataTypeChange: (value: number) => void;
            onSave: () => void | Promise<void>;
            onCancel: () => void;
        };
        schemaDialog: {
            isOpen: boolean;
            isSchemaPrimary: boolean;
            newSchemaName: string;
            dependencies: string[];
            schemaFormError: SchemaFormError;
            primarySchemas: ISelectSchema[];
            onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
            onPrimarySchemaChange: (event: any) => void;
            onSave: () => void | Promise<void>;
            onCancel: () => void;
            onBeforeClose: (arg: BeforeCloseEventArgs) => void;
            getSchemaById: (schemaId: string | null | undefined) => ISchema | null;
        };
        assetClassDialog: {
            isOpen: boolean;
            mode: 'assetClass' | 'assetClassExtension' | 'annotationClass';
            editAssetClass: IAssetClassUpdateDetail | null;
            hasAssetClassError: boolean;
            newAssetClassName: string;
            availablePrimaryAssetClasses: Array<{ id: string; name: string }>;
            selectedPrimaryAssetClassID: string;
            assetClassInputRef: RefObject<HTMLInputElement | null>;
            onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
            onPrimaryAssetClassChange: (value: string) => void;
            onSave: () => void | Promise<void>;
            onCancel: () => void;
        };
        friendClassDialog: {
            isOpen: boolean;
            editFriendClass: IFriendClass | null;
            hasFriendClassError: boolean;
            newFriendClassName: string;
            onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
            onSave: () => void;
            onCancel: () => void;
        };
    };
}

const useConfigurationForm = (): UseConfigurationFormResult => {
    const initialSchemas = FacilityStore.getSchemas();
    const getInitialSchemaExtensions = (primarySchemaId: string | null | undefined): ISchema[] => {
        if (!primarySchemaId) {
            return [];
        }

        return sortByName(initialSchemas.filter((schema) => schema.IsDependent && schema.Dependencies?.includes(primarySchemaId)));
    };

    const initialCollapsedTreeNodeKeys = new Set(
        sortByName(initialSchemas.filter((schema) => !schema.IsDependent)).flatMap((schema) => collectConfigurationTreeNodeKeys({
            schema,
            getSchemaExtensions: getInitialSchemaExtensions
        }))
    );

    const attributeDataTypeLabels = useMemo(() => getAttributeDataTypeLabels(), []);
    const pendingTreeSelectionRef = useRef<IPendingTreeSelection | null>(null);
    const treeGridRef = useRef<TreeGridComponent | null>(null);
    const assetClassInputRef = useRef<HTMLInputElement | null>(null);
    const currentSelectedSchemaIdRef = useRef<string | null>(null);
    const assetClassFetchPromisesRef = useRef<Record<string, Promise<IAssetClassUpdateDetail[]>>>({});
    const annotationClassFetchPromisesRef = useRef<Record<string, Promise<IAssetClassUpdateDetail[]>>>({});
    const treeAssetClassesBySchemaRef = useRef<Record<string, IAssetClassUpdateDetail[]>>({});
    const treeAnnotationClassesBySchemaRef = useRef<Record<string, IAssetClassUpdateDetail[]>>({});
    const currentSelectedClassNodeTypeRef = useRef<'assetClass' | 'annotationClass' | null>(null);
    const lifecycleGenerationRef = useRef(0);

    const [isActionInProgress, setIsActionInProgress] = useState<boolean>(getIsActionInProgress());
    const [actionInProgressMsg, setActionInProgressMsg] = useState<string>(getSavingProgressMessage());
    const [configuration, setConfiguration] = useState<IAssetClassUpdateDetail | null>(FacilityStore.getConfiguration());
    const [allSchemas, setAllSchemas] = useState<ISchema[]>(initialSchemas);
    const [primarySchemas, setPrimarySchemas] = useState<ISelectSchema[]>([]);
    const [assetClasses, setAssetClasses] = useState<IAssetClassUpdateDetail[]>(((FacilityStore.getAssetClasses() as IAssetClassUpdateDetail[]) || []));
    const [allFacilities, setAllFacilities] = useState<IFacility[]>(FacilityStore.getAll());
    const [selectedSchema, setSelectedSchema] = useState<ISchema | null>(null);
    const [selectedSubSchema, setSelectedSubSchema] = useState<ISchema | null>(null);
    const [attributeDefinitions, setAttributeDefinitions] = useState<IAttributeDefinition[]>(FacilityStore.getConfiguration()?.AttributeDefinitions || []);
    const [hasSchemaChanges, setHasSchemaChanges] = useState(FacilityStore.getHasSchemaChanges());
    const [treeAssetClassesBySchema, setTreeAssetClassesBySchema] = useState<Record<string, IAssetClassUpdateDetail[]>>({});
    const [treeAnnotationClassesBySchema, setTreeAnnotationClassesBySchema] = useState<Record<string, IAssetClassUpdateDetail[]>>({});
    const [collapsedTreeNodeKeys, setCollapsedTreeNodeKeys] = useState<Set<string>>(initialCollapsedTreeNodeKeys);
    const [selectedTreeNodeKey, setSelectedTreeNodeKey] = useState<string | null>(null);
    const [selectedAssetClassTab, setSelectedAssetClassTab] = useState<number>(0);
    const [treeContextMenu, setTreeContextMenu] = useState<ITreeContextMenuState>({ visible: false, left: -1000, top: -1000, node: null });
    const [treeGridRenderKey, setTreeGridRenderKey] = useState(0);
    const [isAddSchemaDialogOpen, setIsAddSchemaDialogOpen] = useState(false);
    const [dependencies, setDependencies] = useState<string[]>([]);
    const [newSchemaName, setNewSchemaName] = useState('');
    const [hasSchemaChange, setHasSchemaChange] = useState(false);
    const [schemaFormError, setSchemaFormError] = useState<SchemaFormError>({ schemaName: false, primarySchema: false });
    const [isSchemaPrimary, setIsSchemaPrimary] = useState(true);
    const [isAssetClassDialogOpen, setIsAssetClassDialogOpen] = useState(false);
    const [assetClassDialogMode, setAssetClassDialogMode] = useState<'assetClass' | 'assetClassExtension' | 'annotationClass'>('assetClass');
    const [editAssetClass, setEditAssetClass] = useState<IAssetClassUpdateDetail | null>(null);
    const [newAssetClassName, setNewAssetClassName] = useState('');
    const [availablePrimaryAssetClasses, setAvailablePrimaryAssetClasses] = useState<Array<{ id: string; name: string }>>([]);
    const [selectedPrimaryAssetClassID, setSelectedPrimaryAssetClassID] = useState('');
    const [hasAssetClassError, setHasAssetClassError] = useState(false);
    const [isAddAttributeDialogOpen, setIsAddAttributeDialogOpen] = useState(false);
    const [editAttribute, setEditAttribute] = useState<IAttributeDefinition | null>(null);
    const [newAttributeName, setNewAttributeName] = useState('');
    const [newAttributeDataType, setNewAttributeDataType] = useState(AttributeDataType.String);
    const [hasAddAttributeError, setHasAddAttributeError] = useState(false);
    const [isFriendClassDialogOpen, setIsFriendClassDialogOpen] = useState(false);
    const [editFriendClass, setEditFriendClass] = useState<IFriendClass | null>(null);
    const [newFriendClassName, setNewFriendClassName] = useState('');
    const [hasFriendClassError, setHasFriendClassError] = useState(false);
    const [schemaDetailsForm, setSchemaDetailsForm] = useState<{ schemaName: string; assetClassName: string }>({ schemaName: '', assetClassName: '' });
    const [schemaDetailsErrors, setSchemaDetailsErrors] = useState<{ schemaName: boolean; assetClassName: boolean }>({ schemaName: false, assetClassName: false });
    const [assetSetTaxonomyDetail, setAssetSetTaxonomyDetail] = useState<ILibraryTaxonomyDetail | null>(FacilityStore.getSchemaAssetSetTaxonomyDetail());
    const assetSetTaxonomyOriginalRef = useRef<ILibraryTaxonomyDetail | null>(structuredClone(FacilityStore.getSchemaAssetSetTaxonomyDetail()));
    const [assetSetTaxonomyResetToken, setAssetSetTaxonomyResetToken] = useState(0);

    useEffect(() => {
        const lifecycleGeneration = ++lifecycleGenerationRef.current;

        return () => {
            if (lifecycleGenerationRef.current === lifecycleGeneration) {
                lifecycleGenerationRef.current += 1;
            }
        };
    }, []);

    const loadState = useCallback(() => {
        setIsActionInProgress(getIsActionInProgress());
        setActionInProgressMsg(getSavingProgressMessage());

        const nextConfiguration = FacilityStore.getConfiguration();
        const nextAssetClasses = cloneAssetClasses(FacilityStore.getAssetClasses() as IAssetClassUpdateDetail[]);
        const currentSelectedSchemaId = currentSelectedSchemaIdRef.current;
        const currentCachedAssetClasses = currentSelectedSchemaId ? treeAssetClassesBySchemaRef.current[currentSelectedSchemaId] : null;
        const shouldKeepCachedAssetClasses = !!currentSelectedSchemaId
            && nextAssetClasses.length === 0
            && !!currentCachedAssetClasses?.length
            && nextConfiguration?.SchemaID === currentSelectedSchemaId;

        setConfiguration(nextConfiguration);
        setAttributeDefinitions(nextConfiguration?.AttributeDefinitions || []);
        setAllSchemas(FacilityStore.getSchemas());

        if (currentSelectedClassNodeTypeRef.current !== 'annotationClass' && !shouldKeepCachedAssetClasses && (!currentSelectedSchemaId || nextAssetClasses.every((item) => item.SchemaID === currentSelectedSchemaId))) {
            setAssetClasses(nextAssetClasses);
        }

        setAllFacilities(FacilityStore.getAll());
        setHasSchemaChanges(FacilityStore.getHasSchemaChanges());
        setAssetSetTaxonomyDetail(FacilityStore.getSchemaAssetSetTaxonomyDetail());
    }, []);

    const systemChange = useCallback(() => {
        setIsActionInProgress(getIsActionInProgress());
    }, []);

    const selectedEditableSchema = selectedSubSchema || selectedSchema;
    const selectedNodeType: IConfigurationTreeNode['NodeType'] | null = selectedTreeNodeKey?.startsWith('annotation-class-')
        ? 'annotationClass'
        : selectedTreeNodeKey?.startsWith('asset-class-')
            ? (configuration?.PrimaryAssetClassID ? 'assetClassExtension' : 'assetClass')
            : selectedSubSchema
            ? 'schemaExtension'
            : selectedSchema
                ? 'schema'
                : null;
    const currentEditableSchemaName = selectedEditableSchema?.Name || '';
    const currentEditableAssetClassName = (selectedNodeType === 'assetClass' || selectedNodeType === 'annotationClass') ? configuration?.Name || '' : '';
    const hasSchemaNameDetailsChanges = (selectedNodeType === 'schema' || selectedNodeType === 'schemaExtension') && schemaDetailsForm.schemaName !== currentEditableSchemaName;
    const hasAssetClassNameDetailsChanges = (selectedNodeType === 'assetClass' || selectedNodeType === 'annotationClass') && schemaDetailsForm.assetClassName !== currentEditableAssetClassName;
    const hasDetailsPanelChanges = hasSchemaNameDetailsChanges || hasAssetClassNameDetailsChanges;
    const hasDetailsPanelChangesRef = useRef(false);
    hasDetailsPanelChangesRef.current = hasDetailsPanelChanges;
    const hasAutoPopulateClassificationError = !!configuration?.AutoPopulateAssetSets
        && (configuration.FriendClasses?.length ?? 0) > 0
        && (configuration.AutoPopulateAssetSetClassificationTags?.length ?? 0) === 0;
    const combinedHasSchemaChanges = hasSchemaChanges || hasDetailsPanelChanges;

    const checkForUnsavedChanges = useCallback((event: any) => {
        if (FacilityStore.getHasSchemaChanges() || hasDetailsPanelChangesRef.current) {
            event.returnValue = InovxLoader.t('common:msgBox.browserSaveChanges');
        }
    }, []);

    useEffect(() => {
        FacilityStore.addChangeListener(loadState);
        SystemStore.addChangeListener(systemChange);
        window.addEventListener('beforeunload', checkForUnsavedChanges);

        return () => {
            FacilityStore.removeChangeListener(loadState);
            SystemStore.removeChangeListener(systemChange);
            window.removeEventListener('beforeunload', checkForUnsavedChanges);
        };
    }, [checkForUnsavedChanges, loadState, systemChange]);

    useEffect(() => {
        treeAssetClassesBySchemaRef.current = treeAssetClassesBySchema;
    }, [treeAssetClassesBySchema]);

    useEffect(() => {
        treeAnnotationClassesBySchemaRef.current = treeAnnotationClassesBySchema;
    }, [treeAnnotationClassesBySchema]);

    useEffect(() => {
        const currentSchemaId = selectedSubSchema?.ID || selectedSchema?.ID;
        if (!currentSchemaId) {
            currentSelectedSchemaIdRef.current = null;
            setAssetSetTaxonomyDetail(null);
            return;
        }

        currentSelectedSchemaIdRef.current = currentSchemaId;

        const assetClassesMatchSchema = !assetClasses.length || assetClasses.every((item) => item.SchemaID === currentSchemaId);
        if (!assetClassesMatchSchema) {
            return;
        }

        if (currentSelectedClassNodeTypeRef.current === 'annotationClass') {
            const cachedAnnotationClasses = treeAnnotationClassesBySchemaRef.current[currentSchemaId];
            if (!assetClasses.length && cachedAnnotationClasses?.length) {
                return;
            }

            setTreeAnnotationClassesBySchema((current) => {
                if (areTreeAssetClassesEqual(current[currentSchemaId], assetClasses)) {
                    return current;
                }

                return {
                    ...current,
                    [currentSchemaId]: assetClasses || []
                };
            });

            return;
        }

        const cachedAssetClasses = treeAssetClassesBySchemaRef.current[currentSchemaId];
        if (!assetClasses.length && cachedAssetClasses?.length) {
            return;
        }

        setTreeAssetClassesBySchema((current) => {
            // Avoid rebuilding the tree on plain selection; remounting the TreeGrid resets scroll.
            if (areTreeAssetClassesEqual(current[currentSchemaId], assetClasses)) {
                return current;
            }

            return {
                ...current,
                [currentSchemaId]: assetClasses || []
            };
        });
    }, [assetClasses, selectedSchema?.ID, selectedSubSchema?.ID]);

    useEffect(() => {
        const currentSchemaId = selectedSubSchema?.ID || selectedSchema?.ID;
        if (!currentSchemaId) {
            assetSetTaxonomyOriginalRef.current = null;
            setAssetSetTaxonomyDetail(null);
            return;
        }

        const isEditableSchemaNode = selectedNodeType === 'schema' || selectedNodeType === 'schemaExtension';
        let cancelled = false;
        const loadAssetSetTaxonomy = async () => {
            const taxonomy = await SchemaServices.getAssetSetTaxonomyDetail(currentSchemaId);
            if (cancelled) {
                return;
            }

            SchemaActions.resetAssetSetTaxonomyDetail(taxonomy);
            setAssetSetTaxonomyDetail(taxonomy);

            if (isEditableSchemaNode) {
                assetSetTaxonomyOriginalRef.current = taxonomy ? structuredClone(taxonomy) : null;
                setAssetSetTaxonomyResetToken((current) => current + 1);
            }
            else {
                assetSetTaxonomyOriginalRef.current = null;
            }
        };

        void loadAssetSetTaxonomy();

        return () => {
            cancelled = true;
        };
    }, [selectedNodeType, selectedSchema?.ID, selectedSubSchema?.ID]);

    const getSchemaById = useCallback((schemaId: string | null | undefined): ISchema | null => {
        if (!schemaId) {
            return null;
        }

        return allSchemas.find((schema) => schema.ID === schemaId) || null;
    }, [allSchemas]);

    const getPrimarySchemaForSchema = useCallback((schema: ISchema | null): ISchema | null => {
        if (!schema) {
            return null;
        }

        if (!schema.IsDependent) {
            return schema;
        }

        const parentSchemaId = schema.Dependencies?.[0];
        return allSchemas.find((item) => item.ID === parentSchemaId) || null;
    }, [allSchemas]);

    const getPrimarySchemaForTreeNode = useCallback((node: IConfigurationTreeNode | null | undefined): ISchema | null => {
        if (!node) {
            return null;
        }

        if (node.NodeType === 'schema') {
            return getSchemaById(node.SchemaID);
        }

        const parentSchemaId = getParentSchemaIdFromTreeNodeKey(node.NodeType === 'schemaExtension' ? node.TreeNodeKey : node.ParentTreeNodeKey);
        return getSchemaById(parentSchemaId) || getPrimarySchemaForSchema(getSchemaById(node.SchemaID));
    }, [getPrimarySchemaForSchema, getSchemaById]);

    const getSchemaExtensions = useCallback((primarySchemaId: string | null | undefined): ISchema[] => {
        if (!primarySchemaId) {
            return [];
        }

        return sortByName(allSchemas.filter((schema) => schema.IsDependent && schema.Dependencies?.includes(primarySchemaId)));
    }, [allSchemas]);

    const collapsibleTreeNodeKeys = useMemo(() => {
        return sortByName(allSchemas.filter((schema) => !schema.IsDependent)).flatMap((schema) => collectConfigurationTreeNodeKeys({
            schema,
            getSchemaExtensions
        }));
    }, [allSchemas, getSchemaExtensions]);

    useEffect(() => {
        setCollapsedTreeNodeKeys((current) => {
            const next = new Set(current);
            let hasChanges = false;

            collapsibleTreeNodeKeys.forEach((treeNodeKey) => {
                if (!next.has(treeNodeKey)) {
                    next.add(treeNodeKey);
                    hasChanges = true;
                }
            });

            return hasChanges ? next : current;
        });
    }, [collapsibleTreeNodeKeys]);

    const requestAssetClassesForSchema = useCallback((schemaId: string): Promise<IAssetClassUpdateDetail[]> => {
        if (assetClassFetchPromisesRef.current[schemaId]) {
            return assetClassFetchPromisesRef.current[schemaId];
        }

        const fetchPromise = SchemaServices.getAssetClassesUpdateData(schemaId)
            .then((schemaAssetClasses) => schemaAssetClasses || []);

        assetClassFetchPromisesRef.current[schemaId] = fetchPromise;
        fetchPromise.finally(() => {
            delete assetClassFetchPromisesRef.current[schemaId];
        });

        return fetchPromise;
    }, []);

    const requestAnnotationClassesForSchema = useCallback((schemaId: string): Promise<IAssetClassUpdateDetail[]> => {
        if (annotationClassFetchPromisesRef.current[schemaId]) {
            return annotationClassFetchPromisesRef.current[schemaId];
        }

        const fetchPromise = SchemaServices.getAnnotationClassesUpdateData(schemaId)
            .then((schemaAnnotationClasses) => schemaAnnotationClasses || []);

        annotationClassFetchPromisesRef.current[schemaId] = fetchPromise;
        fetchPromise.finally(() => {
            delete annotationClassFetchPromisesRef.current[schemaId];
        });

        return fetchPromise;
    }, []);

    const fetchAssetClassesForSchema = useCallback(async (schemaId: string): Promise<IAssetClassUpdateDetail[]> => {
        const lifecycleGeneration = lifecycleGenerationRef.current;
        const schemaAssetClasses = await requestAssetClassesForSchema(schemaId);
        const nextSchemaAssetClasses = cloneAssetClasses(schemaAssetClasses);

        if (lifecycleGenerationRef.current !== lifecycleGeneration) {
            return nextSchemaAssetClasses;
        }

        treeAssetClassesBySchemaRef.current = {
            ...treeAssetClassesBySchemaRef.current,
            [schemaId]: nextSchemaAssetClasses
        };

        setTreeAssetClassesBySchema((current) => {
            return {
                ...current,
                [schemaId]: nextSchemaAssetClasses
            };
        });

        return nextSchemaAssetClasses;
    }, [requestAssetClassesForSchema]);

    const fetchAnnotationClassesForSchema = useCallback(async (schemaId: string): Promise<IAssetClassUpdateDetail[]> => {
        const lifecycleGeneration = lifecycleGenerationRef.current;
        const schemaAnnotationClasses = await requestAnnotationClassesForSchema(schemaId);
        const nextSchemaAnnotationClasses = cloneAssetClasses(schemaAnnotationClasses);

        if (lifecycleGenerationRef.current !== lifecycleGeneration) {
            return nextSchemaAnnotationClasses;
        }

        treeAnnotationClassesBySchemaRef.current = {
            ...treeAnnotationClassesBySchemaRef.current,
            [schemaId]: nextSchemaAnnotationClasses
        };

        setTreeAnnotationClassesBySchema((current) => {
            return {
                ...current,
                [schemaId]: nextSchemaAnnotationClasses
            };
        });

        return nextSchemaAnnotationClasses;
    }, [requestAnnotationClassesForSchema]);

    const loadItemClassesForSchema = useCallback(async (schemaId: string, classNodeType: 'assetClass' | 'annotationClass', preferredAssetClassId?: string | null) => {
        const lifecycleGeneration = lifecycleGenerationRef.current;
        const currentCache = classNodeType === 'annotationClass' ? treeAnnotationClassesBySchema : treeAssetClassesBySchema;
        const hasCachedItemClasses = Object.prototype.hasOwnProperty.call(currentCache, schemaId);
        let currentItemClasses = hasCachedItemClasses ? currentCache[schemaId] : null;

        if (!hasCachedItemClasses) {
            currentItemClasses = classNodeType === 'annotationClass'
                ? await fetchAnnotationClassesForSchema(schemaId)
                : await fetchAssetClassesForSchema(schemaId);
        }

        const resolvedItemClasses = cloneAssetClasses(currentItemClasses);
        if (lifecycleGenerationRef.current !== lifecycleGeneration) {
            return resolvedItemClasses;
        }

        currentSelectedClassNodeTypeRef.current = classNodeType;
        setAssetClasses(resolvedItemClasses);

        const selectedClass = preferredAssetClassId
            ? resolvedItemClasses.find((item) => item.ID === preferredAssetClassId) || resolvedItemClasses[0]
            : resolvedItemClasses[0];

        SchemaActions.setConfigurationData(selectedClass || null);
        return resolvedItemClasses;
    }, [fetchAnnotationClassesForSchema, fetchAssetClassesForSchema, treeAnnotationClassesBySchema, treeAssetClassesBySchema]);

    const applySchemaSelection = useCallback(async (
        primarySchema: ISchema | null,
        schemaExtension: ISchema | null,
        preferredAssetClassId?: string | null,
        treeNodeKey?: string | null,
        preferredNodeType: 'assetClass' | 'annotationClass' = 'assetClass'
    ) => {
        const targetSchemaId = schemaExtension?.ID || primarySchema?.ID;
        currentSelectedSchemaIdRef.current = targetSchemaId || null;
        setSelectedSchema(primarySchema);
        setSelectedSubSchema(schemaExtension);

        if (treeNodeKey !== undefined) {
            setSelectedTreeNodeKey(treeNodeKey);
        }

        if (!targetSchemaId) {
            SchemaActions.setConfigurationData(null);
            return;
        }

        await loadItemClassesForSchema(targetSchemaId, preferredNodeType, preferredAssetClassId);
    }, [loadItemClassesForSchema]);

    const missingAssetTreeSchemaIds = useMemo(() => {
        const knownSchemaIds = new Set(Object.keys(treeAssetClassesBySchema));
        return (allSchemas || []).map((schema) => schema.ID).filter((schemaId) => !knownSchemaIds.has(schemaId));
    }, [allSchemas, treeAssetClassesBySchema]);

    const missingAnnotationTreeSchemaIds = useMemo(() => {
        const knownSchemaIds = new Set(Object.keys(treeAnnotationClassesBySchema));
        return (allSchemas || []).map((schema) => schema.ID).filter((schemaId) => !knownSchemaIds.has(schemaId));
    }, [allSchemas, treeAnnotationClassesBySchema]);

    useEffect(() => {
        if (!missingAssetTreeSchemaIds.length) {
            return;
        }

        let isCancelled = false;

        const prefetchSchemaAssetClasses = async () => {
            const entries = await Promise.all(missingAssetTreeSchemaIds.map(async (schemaId) => ([schemaId, await requestAssetClassesForSchema(schemaId)] as const)));

            if (isCancelled) {
                return;
            }

            setTreeAssetClassesBySchema((current) => {
                const next = { ...current };
                let hasChanges = false;

                entries.forEach(([schemaId, schemaAssetClasses]) => {
                    if (Object.prototype.hasOwnProperty.call(next, schemaId)) {
                        return;
                    }

                    next[schemaId] = schemaAssetClasses;
                    hasChanges = true;
                });

                return hasChanges ? next : current;
            });
        };

        void prefetchSchemaAssetClasses();

        return () => {
            isCancelled = true;
        };
    }, [missingAssetTreeSchemaIds, requestAssetClassesForSchema]);

    useEffect(() => {
        if (!missingAnnotationTreeSchemaIds.length) {
            return;
        }

        let isCancelled = false;

        const prefetchAnnotationClasses = async () => {
            const entries = await Promise.all(missingAnnotationTreeSchemaIds.map(async (schemaId) => ([schemaId, await requestAnnotationClassesForSchema(schemaId)] as const)));

            if (isCancelled) {
                return;
            }

            setTreeAnnotationClassesBySchema((current) => {
                const next = { ...current };
                let hasChanges = false;

                entries.forEach(([schemaId, schemaAnnotationClasses]) => {
                    if (Object.prototype.hasOwnProperty.call(next, schemaId)) {
                        return;
                    }

                    next[schemaId] = schemaAnnotationClasses;
                    hasChanges = true;
                });

                return hasChanges ? next : current;
            });
        };

        void prefetchAnnotationClasses();

        return () => {
            isCancelled = true;
        };
    }, [missingAnnotationTreeSchemaIds, requestAnnotationClassesForSchema]);

    useEffect(() => {
        const filteredPrimarySchemas = sortByName(allSchemas.filter((schema) => !schema.IsDependent));
        setPrimarySchemas(filteredPrimarySchemas.map((schema) => ({ id: schema.ID, name: schema.Name })));

        const initializeSelection = async () => {
            if (!filteredPrimarySchemas.length) {
                setSelectedSchema(null);
                setSelectedSubSchema(null);
                setSelectedTreeNodeKey(null);
                SchemaActions.setConfigurationData(null);
                return;
            }

            const pendingSelection = pendingTreeSelectionRef.current;
            if (pendingSelection) {
                pendingTreeSelectionRef.current = null;
                const primarySchema = getSchemaById(pendingSelection.primarySchemaId);
                const schemaExtension = getSchemaById(pendingSelection.schemaId);
                expandTreeNodeKey(pendingSelection.expandedTreeNodeKey);
                await applySchemaSelection(primarySchema, schemaExtension?.IsDependent ? schemaExtension : null, null, pendingSelection.treeNodeKey);
                return;
            }

            if (selectedSchema) {
                const refreshedPrimarySchema = getSchemaById(selectedSchema.ID);
                const refreshedSchemaExtension = selectedSubSchema ? getSchemaById(selectedSubSchema.ID) : null;

                if (refreshedPrimarySchema) {
                    const preferredNodeType = getItemClassNodeTypeFromTreeNodeKey(selectedTreeNodeKey);
                    await applySchemaSelection(
                        refreshedPrimarySchema,
                        refreshedSchemaExtension,
                        configuration?.SchemaID === (refreshedSchemaExtension?.ID || refreshedPrimarySchema.ID) ? configuration?.ID : null,
                        selectedTreeNodeKey || `schema-${refreshedPrimarySchema.ID}`,
                        preferredNodeType
                    );
                    return;
                }
            }

            const firstPrimarySchema = filteredPrimarySchemas[0];
            await applySchemaSelection(firstPrimarySchema, null, null, `schema-${firstPrimarySchema.ID}`);
        };

        void initializeSelection();
    }, [allSchemas]);

    useEffect(() => {
        setSelectedAssetClassTab(0);
    }, [selectedTreeNodeKey]);

    useEffect(() => {
        if (!isAssetClassDialogOpen || assetClassDialogMode !== 'assetClass' || document.activeElement === assetClassInputRef.current) {
            return;
        }

        const animationFrameId = window.requestAnimationFrame(() => {
            assetClassInputRef.current?.focus();
        });

        return () => {
            window.cancelAnimationFrame(animationFrameId);
        };
    }, [assetClassDialogMode, isAssetClassDialogOpen]);

    const hideTreeContextMenu = () => {
        setTreeContextMenu((current) => current.visible ? { visible: false, left: -1000, top: -1000, node: null } : current);
    };

    useEffect(() => {
        setSchemaDetailsForm({
            schemaName: currentEditableSchemaName,
            assetClassName: currentEditableAssetClassName
        });
        setSchemaDetailsErrors({ schemaName: false, assetClassName: false });
    }, [currentEditableAssetClassName, currentEditableSchemaName, selectedTreeNodeKey]);

    const resetSchemaDetailsForm = useCallback(() => {
        setSchemaDetailsForm({
            schemaName: currentEditableSchemaName,
            assetClassName: currentEditableAssetClassName
        });
        setSchemaDetailsErrors({ schemaName: false, assetClassName: false });
    }, [currentEditableAssetClassName, currentEditableSchemaName]);

    const expandTreeNodeKey = useCallback((treeNodeKey: string | null | undefined) => {
        if (!treeNodeKey) {
            return;
        }

        setCollapsedTreeNodeKeys((current) => {
            if (!current.has(treeNodeKey)) {
                return current;
            }

            const next = new Set(current);
            next.delete(treeNodeKey);
            return next;
        });
    }, []);

    const remountTreeGrid = useCallback(() => {
        // Syncfusion does not reliably materialize newly inserted child rows from nested datasource updates.
        // Reserve a full remount for structural tree changes only so ordinary selection still preserves scroll.
        setTreeGridRenderKey((current) => current + 1);
    }, []);

    const validateSchemaDetailsSchemaName = useCallback((value: string) => {
        if (!selectedNodeType || isAssetClassNodeType(selectedNodeType)) {
            return false;
        }

        const trimmedValue = value.trim();
        if (!trimmedValue.length) {
            return true;
        }

        if (selectedNodeType === 'schemaExtension' && selectedSubSchema) {
            const dependencyIds = selectedSubSchema.Dependencies || [];
            return allSchemas
                .filter((schema) => schema.IsDependent && schema.ID !== selectedSubSchema.ID && schema.Dependencies?.some((parentId) => dependencyIds.includes(parentId)))
                .some((schema) => schema.Name.toLocaleLowerCase() === trimmedValue.toLocaleLowerCase());
        }

        if (selectedSchema) {
            return allSchemas
                .filter((schema) => !schema.IsDependent && schema.ID !== selectedSchema.ID)
                .some((schema) => schema.Name.toLocaleLowerCase() === trimmedValue.toLocaleLowerCase());
        }

        return false;
    }, [allSchemas, selectedNodeType, selectedSchema, selectedSubSchema]);

    const validateSchemaDetailsAssetClassName = useCallback((value: string) => {
        if ((selectedNodeType !== 'assetClass' && selectedNodeType !== 'annotationClass') || !configuration) {
            return false;
        }

        const trimmedValue = value.trim();
        if (!trimmedValue.length) {
            return true;
        }

        return assetClasses.some((assetClass) => assetClass.ID !== configuration.ID && assetClass.Name.toLocaleLowerCase() === trimmedValue.toLocaleLowerCase());
    }, [assetClasses, configuration, selectedNodeType]);

    const validateSchemaDetailsForm = useCallback(() => {
        const nextErrors = {
            schemaName: validateSchemaDetailsSchemaName(schemaDetailsForm.schemaName),
            assetClassName: validateSchemaDetailsAssetClassName(schemaDetailsForm.assetClassName)
        };

        setSchemaDetailsErrors(nextErrors);
        return !nextErrors.schemaName && !nextErrors.assetClassName;
    }, [schemaDetailsForm.assetClassName, schemaDetailsForm.schemaName, validateSchemaDetailsAssetClassName, validateSchemaDetailsSchemaName]);

    const verifyPendingConfigurationChanges = useCallback(() => {
        if (hasDetailsPanelChangesRef.current) {
            SystemActions.displayInfoMessage({
                message: InovxLoader.t('facility:message.savePendingChangesInfo')
            });
            return false;
        }

        return SchemaActions.verifyConfigurationChanges();
    }, []);

    const saveConfiguration = async () => {
        setActionInProgressMsg(getSavingProgressMessage());

        if (isItemClassNodeType(selectedTreeNode?.NodeType) && configuration) {
            if ((selectedTreeNode?.NodeType === 'assetClass' || selectedTreeNode?.NodeType === 'annotationClass') && !validateSchemaDetailsForm()) {
                return;
            }

            const trimmedAssetClassName = schemaDetailsForm.assetClassName.trim();
            const parentTreeNodeKey = selectedTreeNode.ParentTreeNodeKey || selectedTreeNodeKey;

            if (selectedTreeNode.NodeType === 'annotationClass') {
                currentSelectedClassNodeTypeRef.current = 'annotationClass';
                await SchemaActions.createOrUpdateAnnotationClass({
                    ...configuration,
                    Name: trimmedAssetClassName,
                    AttributeDefinitions: [],
                    FriendClasses: [],
                    AutoPopulateAssetSets: false,
                    AutoPopulateAssetSetClassificationTags: [],
                    PrimaryAssetClassID: null
                });

                const resolvedAnnotationClasses = await fetchAnnotationClassesForSchema(configuration.SchemaID);
                const savedAnnotationClass = resolvedAnnotationClasses.find((item) => item.ID === configuration.ID)
                    || resolvedAnnotationClasses.find((item) => item.Name.toLocaleLowerCase() === trimmedAssetClassName.toLocaleLowerCase());

                if (savedAnnotationClass) {
                    setAssetClasses(resolvedAnnotationClasses);
                    SchemaActions.setConfigurationData(savedAnnotationClass);
                }

                if (savedAnnotationClass && parentTreeNodeKey) {
                    setSelectedTreeNodeKey(`annotation-class-${savedAnnotationClass.ID}-parent-${parentTreeNodeKey}`);
                }
            } else {
                if (hasInvalidExternalAttributeState) {
                    return;
                }

                currentSelectedClassNodeTypeRef.current = 'assetClass';
                await SchemaActions.createOrUpdateAssetClass({
                    ...configuration,
                    Name: selectedTreeNode.NodeType === 'assetClass' ? trimmedAssetClassName : configuration.Name
                });

                const resolvedAssetClasses = await fetchAssetClassesForSchema(configuration.SchemaID);
                const savedAssetClass = resolvedAssetClasses.find((item) => item.ID === configuration.ID)
                    || (selectedTreeNode.NodeType === 'assetClass'
                        ? resolvedAssetClasses.find((item) => item.Name.toLocaleLowerCase() === trimmedAssetClassName.toLocaleLowerCase())
                        : resolvedAssetClasses.find((item) => item.PrimaryAssetClassID === configuration.PrimaryAssetClassID));

                if (savedAssetClass && parentTreeNodeKey) {
                    setSelectedTreeNodeKey(`asset-class-${savedAssetClass.ID}-parent-${parentTreeNodeKey}`);
                }
            }

            remountTreeGrid();

            return;
        }

        if (!selectedTreeNode || !selectedEditableSchema) {
            return;
        }

        if (!validateSchemaDetailsForm()) {
            return;
        }

        const trimmedSchemaName = schemaDetailsForm.schemaName.trim();
        const shouldUpdateSchema = trimmedSchemaName !== currentEditableSchemaName;

        if (shouldUpdateSchema) {
            const collapsedTreeNodeKeysSnapshot = new Set(collapsedTreeNodeKeys);

            await SchemaActions.updateSchema({
                ...selectedEditableSchema,
                Name: trimmedSchemaName
            });
            setTimeout(() => {  
                setCollapsedTreeNodeKeys(collapsedTreeNodeKeysSnapshot);
            }, 10);
        }

        if (assetSetTaxonomyDetail?.ID) {
            const persistedTaxonomy = await SchemaActions.persistAssetSetTaxonomyDetail(selectedEditableSchema.ID, assetSetTaxonomyDetail);
            if (persistedTaxonomy) {
                assetSetTaxonomyOriginalRef.current = structuredClone(persistedTaxonomy);
            }
        }
    };

    const cancel = async () => {
        const classType = selectedTreeNode?.NodeType === 'annotationClass' ? 'annotationClass' : 'assetClass';
        const isSchemaNode = selectedNodeType === 'schema' || selectedNodeType === 'schemaExtension';

        if (isSchemaNode && (FacilityStore.getHasSchemaChanges() || hasDetailsPanelChanges)) {
            SystemActions.displaySaveChangesDialog({
                message: InovxLoader.t('common:msgBox.saveChanges'),
                confirmCallback: async () => {
                    await saveConfiguration();
                },
                cancelCallback: async () => {
                    resetSchemaDetailsForm();
                    await SchemaActions._resetConfigurationData(null, classType);
                    resetAssetSetTaxonomyChanges();
                }
            });
            return;
        }

        resetSchemaDetailsForm();
        if (isSchemaNode) {
            resetAssetSetTaxonomyChanges();
            await SchemaActions._resetConfigurationData(null, classType);
            return;
        }

        SchemaActions.cancelEdit(classType);
    };

    const onSelectChanged = (event: any) => {
        ConfigurationActions.editConfigurationProperty(ConfigurationConstants.CONFIGURATION_FORM_TAGDISPLAYDATA, event.target.name, event.target.value);
    };

    const changeColor = (name: string, color: string) => {
        ConfigurationActions.editConfigurationProperty(ConfigurationConstants.CONFIGURATION_FORM_TAGDISPLAYDATA, name, color);
    };

    const openAddSchemaDialog = (primary: boolean, defaultDependencies: string[] = []) => {
        setNewSchemaName('');
        setSchemaFormError({ schemaName: false, primarySchema: false });
        setIsSchemaPrimary(primary);
        setDependencies(defaultDependencies);
        setHasSchemaChange(false);
        setIsAddSchemaDialogOpen(true);
    };

    const onAddSchema = () => {
        if (!verifyPendingConfigurationChanges()) {
            return;
        }

        openAddSchemaDialog(true);
    };

    const handleSchemaDetailsSchemaNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSchemaDetailsForm((current) => ({
            ...current,
            schemaName: value
        }));
        setSchemaDetailsErrors((current) => ({
            ...current,
            schemaName: validateSchemaDetailsSchemaName(value)
        }));
    };

    const handleSchemaDetailsAssetClassNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSchemaDetailsForm((current) => ({
            ...current,
            assetClassName: value
        }));
        setSchemaDetailsErrors((current) => ({
            ...current,
            assetClassName: validateSchemaDetailsAssetClassName(value)
        }));
    };

    const handleAssetSetTaxonomyChange = (taxonomy: ILibraryTaxonomyDetail) => {
        setAssetSetTaxonomyDetail(taxonomy);
        SchemaActions.updateAssetSetTaxonomyDetail(taxonomy);
    };

    const resetAssetSetTaxonomyChanges = () => {
        const originalAssetSetTaxonomy = assetSetTaxonomyOriginalRef.current ? structuredClone(assetSetTaxonomyOriginalRef.current) : null;
        setAssetSetTaxonomyDetail(originalAssetSetTaxonomy);
        SchemaActions.resetAssetSetTaxonomyDetail(originalAssetSetTaxonomy);
        setAssetSetTaxonomyResetToken((current) => current + 1);
    };

    const handleSchemaInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setNewSchemaName(event.target.value);
        setHasSchemaChange(true);
    };

    const onPrimarySchemaChange = (event: any) => {
        setDependencies(event.value || []);
        setHasSchemaChange(true);
    };

    const isSchemaFormValid = () => {
        let valid = true;
        const formError = { schemaName: false, primarySchema: false };
        const trimmedValue = newSchemaName.trim();

        if (trimmedValue.length > 0) {
            let isUnique = true;

            if (isSchemaPrimary) {
                isUnique = allSchemas
                    .filter((schema) => !schema.IsDependent)
                    .every((schema) => schema.Name.toLocaleLowerCase() !== trimmedValue.toLocaleLowerCase());
            } else {
                isUnique = allSchemas
                    .filter((schema) => schema.IsDependent && schema.Dependencies?.some((parentId) => dependencies.includes(parentId)))
                    .every((schema) => schema.Name.toLocaleLowerCase() !== trimmedValue.toLocaleLowerCase());
            }

            if (!isUnique) {
                formError.schemaName = true;
                valid = false;
            }
        } else {
            formError.schemaName = true;
            valid = false;
        }

        if (!isSchemaPrimary && dependencies.length === 0) {
            formError.primarySchema = true;
            valid = false;
        }

        setSchemaFormError(formError);
        return valid;
    };

    const handleSaveSchema = async () => {
        if (!isSchemaFormValid()) {
            return;
        }

        const schemaInput: ISchema = {
            ID: null,
            Name: newSchemaName.trim(),
            Dependencies: isSchemaPrimary ? [] : dependencies,
            IsDependent: !isSchemaPrimary
        };

        SchemaActions.createSchema(schemaInput, (newSchema) => {
            const primarySchemaId = newSchema.IsDependent ? (newSchema.Dependencies?.[0] || dependencies[0]) : newSchema.ID;
            pendingTreeSelectionRef.current = {
                primarySchemaId,
                schemaId: newSchema.ID,
                treeNodeKey: newSchema.IsDependent ? `schema-extension-${newSchema.ID}-parent-${primarySchemaId}` : `schema-${newSchema.ID}`,
                expandedTreeNodeKey: newSchema.IsDependent ? `schema-${primarySchemaId}` : null
            };
        });

        setIsAddSchemaDialogOpen(false);
    };

    const finishSchema = () => {
        setIsAddSchemaDialogOpen(false);
        setNewSchemaName('');
        setSchemaFormError({ schemaName: false, primarySchema: false });
        setIsSchemaPrimary(true);
        setDependencies([]);
        setHasSchemaChange(false);
    };

    const handleCancelSchema = () => {
        if (hasSchemaChange) {
            SystemActions.displaySaveChangesDialog({
                message: InovxLoader.t('common:msgBox.saveChanges'),
                confirmCallback: () => {
                    if (isSchemaFormValid()) {
                        handleSaveSchema();
                    }
                },
                cancelCallback: finishSchema
            });
            return;
        }

        finishSchema();
    };

    const handleBeforeClose = (arg: BeforeCloseEventArgs) => {
        if (hasSchemaChange) {
            arg.cancel = true;
            SystemActions.displaySaveChangesDialog({
                message: InovxLoader.t('common:msgBox.saveChanges'),
                confirmCallback: () => {
                    if (isSchemaFormValid()) {
                        handleSaveSchema();
                    }
                },
                cancelCallback: finishSchema
            });
            return;
        }

        if (isAddSchemaDialogOpen) {
            finishSchema();
        }
    };

    const onAddAttribute = () => {
        setEditAttribute(null);
        setNewAttributeName('');
        setNewAttributeDataType(AttributeDataType.String);
        setHasAddAttributeError(true);
        setIsAddAttributeDialogOpen(true);
    };

    const onEditAttribute = (id: string) => {
        const attribute = { ...attributeDefinitions.find((item) => item.ID === id) };
        setEditAttribute(attribute);
        setNewAttributeName(attribute.Name);
        setNewAttributeDataType(attribute.DataType);
        setHasAddAttributeError(false);
        setIsAddAttributeDialogOpen(true);
    };

    const handleAddAttributeInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const trimmedValue = value.trim();
        let hasError = true;

        if (trimmedValue.length > 0) {
            const isUnique = attributeDefinitions.every((attribute) => attribute.Name.toLocaleLowerCase() !== trimmedValue.toLocaleLowerCase() || attribute.ID === editAttribute?.ID);
            if (isUnique) {
                hasError = false;
            }
        }

        setHasAddAttributeError(hasError);
        setNewAttributeName(value);
    };

    const handleSaveNewAttribute = async () => {
        const changedAttributes = [...attributeDefinitions];

        if (editAttribute === null) {
            changedAttributes.push({
                ID: `temp_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
                Name: newAttributeName.trim(),
                DataType: newAttributeDataType
            });
        } else {
            const index = changedAttributes.findIndex((attribute) => attribute.ID === editAttribute.ID);
            if (index !== -1) {
                changedAttributes[index] = {
                    ...changedAttributes[index],
                    Name: newAttributeName.trim(),
                    DataType: newAttributeDataType
                };
            }
        }

        ConfigurationActions.editConfigurationProperty(ConfigurationConstants.CONFIGURATION_FORM_ATTRIBUTEDATA, 'AttributeDefinitions', changedAttributes);
        setIsAddAttributeDialogOpen(false);
        setEditAttribute(null);
    };

    const handleCancelAddAttribute = () => {
        setIsAddAttributeDialogOpen(false);
        setEditAttribute(null);
    };

    const handleDeleteAttribute = (id: string) => {
        const deleteAttributeHandler = () => {
            const changedAttributes = [...attributeDefinitions];
            const index = changedAttributes.findIndex((attribute) => attribute.ID === id);
            if (index !== -1) {
                changedAttributes.splice(index, 1);
            }

            ConfigurationActions.editConfigurationProperty(ConfigurationConstants.CONFIGURATION_FORM_ATTRIBUTEDATA, 'AttributeDefinitions', changedAttributes);
        };

        if (isNewAttributeID(id)) {
            deleteAttributeHandler();
            return;
        }

        SystemActions.displayConfirmationMessage({
            message: InovxLoader.t('facility:message.confirmAttributeDeletion'),
            confirmCallback: deleteAttributeHandler
        });
    };

    const onAddFriendClass = () => {
        setEditFriendClass(null);
        setNewFriendClassName('');
        setHasFriendClassError(true);
        setIsFriendClassDialogOpen(true);
    };

    const onEditFriendClass = (name: string) => {
        const friendClass = configuration?.FriendClasses?.find((item) => item.Name === name);
        if (!friendClass) {
            return;
        }

        setEditFriendClass({ ...friendClass });
        setNewFriendClassName(friendClass.Name);
        setHasFriendClassError(false);
        setIsFriendClassDialogOpen(true);
    };

    const handleFriendClassInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const trimmedValue = value.trim();
        let hasError = true;

        if (trimmedValue.length > 0) {
            const existingFriendClasses = configuration?.FriendClasses || [];
            const trimmedLower = trimmedValue.toLocaleLowerCase();
            const currentLower = editFriendClass?.Name?.toLocaleLowerCase();
            const isUnique = existingFriendClasses.every((friendClass) => {
                const friendLower = friendClass.Name.toLocaleLowerCase();
                if (friendLower === trimmedLower) {
                    return friendLower === currentLower;
                }

                return true;
            });

            if (isUnique) {
                hasError = false;
            }
        }

        setHasFriendClassError(hasError);
        setNewFriendClassName(value);
    };

    const handleSaveFriendClass = () => {
        const trimmedName = newFriendClassName.trim();
        if (!trimmedName) {
            return;
        }

        const updatedFriendClasses = [...(configuration?.FriendClasses || [])];
        if (editFriendClass === null) {
            updatedFriendClasses.push({ Name: trimmedName });
        } else {
            const index = updatedFriendClasses.findIndex((friendClass) => friendClass.Name.toLocaleLowerCase() === editFriendClass.Name.toLocaleLowerCase());
            if (index !== -1) {
                updatedFriendClasses[index] = { Name: trimmedName };
            }
        }

        ConfigurationActions.editConfigurationProperty(ConfigurationConstants.CONFIGURATION_FORM_FRIENDCLASSES, 'FriendClasses', updatedFriendClasses);
        setIsFriendClassDialogOpen(false);
        setEditFriendClass(null);
        setNewFriendClassName('');
        setHasFriendClassError(false);
    };

    const handleCancelFriendClass = () => {
        setIsFriendClassDialogOpen(false);
        setEditFriendClass(null);
        setNewFriendClassName('');
        setHasFriendClassError(false);
    };

    const handleDeleteFriendClass = (name: string) => {
        SystemActions.displayConfirmationMessage({
            message: InovxLoader.t('facility:message.confirmFriendClassDeletion'),
            confirmCallback: () => {
                const updatedFriendClasses = (configuration?.FriendClasses || []).filter((friendClass) => friendClass.Name !== name);
                ConfigurationActions.editConfigurationProperty(ConfigurationConstants.CONFIGURATION_FORM_FRIENDCLASSES, 'FriendClasses', updatedFriendClasses);
            }
        });
    };

    const handleAutoPopulateAssetSetsChange = (enabled: boolean) => {
        ConfigurationActions.editConfigurationProperty(ConfigurationConstants.CONFIGURATION_FORM_FRIENDCLASSES, 'AutoPopulateAssetSets', enabled);
        if (!enabled) {
            ConfigurationActions.editConfigurationProperty(ConfigurationConstants.CONFIGURATION_FORM_FRIENDCLASSES, 'AutoPopulateAssetSetClassificationTags', []);
        }
    };

    const handleAssetSetClassificationTagsChange = (selectedTags: string[]) => {
        ConfigurationActions.editConfigurationProperty(ConfigurationConstants.CONFIGURATION_FORM_FRIENDCLASSES, 'AutoPopulateAssetSetClassificationTags', selectedTags);
    };

    const openAddAssetClassDialog = () => {
        setAssetClassDialogMode('assetClass');
        setEditAssetClass(null);
        setNewAssetClassName('');
        setAvailablePrimaryAssetClasses([]);
        setSelectedPrimaryAssetClassID('');
        setHasAssetClassError(true);
        setIsAssetClassDialogOpen(true);
    };

    const openAddAnnotationClassDialog = async () => {
        const targetSchemaId = selectedSubSchema?.ID || selectedSchema?.ID;
        if (!targetSchemaId) {
            return;
        }

        const currentAnnotationClasses = await fetchAnnotationClassesForSchema(targetSchemaId);
        setAssetClasses(currentAnnotationClasses);
        currentSelectedClassNodeTypeRef.current = 'annotationClass';
        setAssetClassDialogMode('annotationClass');
        setEditAssetClass(null);
        setNewAssetClassName('');
        setAvailablePrimaryAssetClasses([]);
        setSelectedPrimaryAssetClassID('');
        setHasAssetClassError(true);
        setIsAssetClassDialogOpen(true);
    };

    const openAddAssetClassExtensionDialog = async (schemaExtensionNode?: IConfigurationTreeNode | null) => {
        const targetSchemaExtension = (schemaExtensionNode?.NodeType === 'schemaExtension' ? schemaExtensionNode.Schema : null) || selectedSubSchema;
        const parentSchemaNode = schemaExtensionNode?.NodeType === 'schemaExtension' && schemaExtensionNode.ParentTreeNodeKey
            ? findTreeNodeByKey(treeDataSource, schemaExtensionNode.ParentTreeNodeKey)
            : null;
        const primarySchema = parentSchemaNode?.Schema || getPrimarySchemaForTreeNode(schemaExtensionNode) || selectedSchema || getPrimarySchemaForSchema(targetSchemaExtension || null);

        if (!targetSchemaExtension?.ID || !primarySchema?.ID) {
            return;
        }

        const [currentSchemaAssetClasses, primarySchemaAssetClasses] = await Promise.all([
            fetchAssetClassesForSchema(targetSchemaExtension.ID),
            fetchAssetClassesForSchema(primarySchema.ID)
        ]);

        const currentSchemaTreeAssetClasses = getTreeNodeAssetClasses(schemaExtensionNode);
        const currentSchemaExtensionSources = currentSchemaAssetClasses.length ? currentSchemaAssetClasses : currentSchemaTreeAssetClasses;
        const primarySchemaTreeAssetClasses = getTreeNodeAssetClasses(parentSchemaNode);
        const primarySchemaSources = primarySchemaAssetClasses.length ? primarySchemaAssetClasses : primarySchemaTreeAssetClasses;

        const alreadyExtendedPrimaryIds = new Set((currentSchemaExtensionSources || [])
            .map((assetClass) => assetClass.PrimaryAssetClassID)
            .filter(Boolean));

        const nextAvailablePrimaryAssetClasses = sortByName((primarySchemaSources || [])
            .filter((assetClass) => !assetClass.PrimaryAssetClassID)
            .filter((assetClass) => !alreadyExtendedPrimaryIds.has(assetClass.ID)))
            .map((assetClass) => ({ id: assetClass.ID, name: assetClass.Name }));

        setAssetClassDialogMode('assetClassExtension');
        setEditAssetClass(null);
        setNewAssetClassName('');
        setAvailablePrimaryAssetClasses(nextAvailablePrimaryAssetClasses);
        setSelectedPrimaryAssetClassID(nextAvailablePrimaryAssetClasses[0]?.id || '');
        setHasAssetClassError(false);
        setIsAssetClassDialogOpen(true);
    };

    const openEditAssetClassDialog = (assetClassToEdit?: IAssetClassUpdateDetail | null) => {
        const selectedClass = assetClassToEdit || assetClasses.find((item) => item.ID === configuration?.ID);
        if (!selectedClass) {
            return;
        }

        setAssetClassDialogMode('assetClass');
        setEditAssetClass({ ...selectedClass });
        setNewAssetClassName(selectedClass.Name);
        setAvailablePrimaryAssetClasses([]);
        setSelectedPrimaryAssetClassID('');
        setHasAssetClassError(false);
        setIsAssetClassDialogOpen(true);
    };

    const openEditAnnotationClassDialog = async (annotationClassToEdit?: IAssetClassUpdateDetail | null) => {
        const targetSchemaId = selectedSubSchema?.ID || selectedSchema?.ID || annotationClassToEdit?.SchemaID;
        if (!targetSchemaId) {
            return;
        }

        const currentAnnotationClasses = await fetchAnnotationClassesForSchema(targetSchemaId);
        const selectedClass = annotationClassToEdit || currentAnnotationClasses.find((item) => item.ID === configuration?.ID);
        if (!selectedClass) {
            return;
        }

        setAssetClasses(currentAnnotationClasses);
        currentSelectedClassNodeTypeRef.current = 'annotationClass';
        setAssetClassDialogMode('annotationClass');
        setEditAssetClass({ ...selectedClass });
        setNewAssetClassName(selectedClass.Name);
        setAvailablePrimaryAssetClasses([]);
        setSelectedPrimaryAssetClassID('');
        setHasAssetClassError(false);
        setIsAssetClassDialogOpen(true);
    };

    const handleAssetClassInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const trimmedValue = value.trim();
        let hasError = true;

        if (trimmedValue.length > 0) {
            const isUnique = assetClasses.every((assetClass) => assetClass.Name.toLocaleLowerCase() !== trimmedValue.toLocaleLowerCase() || assetClass.ID === editAssetClass?.ID);
            if (isUnique) {
                hasError = false;
            }
        }

        setHasAssetClassError(hasError);
        setNewAssetClassName(value);
    };

    const handleSaveAssetClass = async () => {
        const targetSchemaId = selectedSubSchema?.ID || selectedSchema?.ID;
        if (!targetSchemaId) {
            return;
        }

        const parentTreeNodeKey = isItemClassNodeType(selectedTreeNode?.NodeType)
            ? selectedTreeNode.ParentTreeNodeKey || selectedTreeNodeKey
            : selectedTreeNodeKey;

        setIsAssetClassDialogOpen(false);
        setEditAssetClass(null);

        const selectedPrimaryAssetClass = availablePrimaryAssetClasses.find((assetClass) => assetClass.id === selectedPrimaryAssetClassID);
        const assetClass = assetClassDialogMode === 'assetClassExtension'
            ? (selectedPrimaryAssetClass
                ? {
                    ...FacilityStore.getBlankAssetClassUpdateDetail(),
                    Name: selectedPrimaryAssetClass.name,
                    SchemaID: targetSchemaId,
                    PrimaryAssetClassID: selectedPrimaryAssetClass.id
                }
                : null)
            : assetClassDialogMode === 'annotationClass'
                ? (editAssetClass === null
                    ? { ...FacilityStore.getBlankAssetClassUpdateDetail(), Name: newAssetClassName.trim(), SchemaID: targetSchemaId, AttributeDefinitions: [], FriendClasses: [], AutoPopulateAssetSets: false, AutoPopulateAssetSetClassificationTags: [], PrimaryAssetClassID: null }
                    : { ...editAssetClass, Name: newAssetClassName.trim(), AttributeDefinitions: [], FriendClasses: [], AutoPopulateAssetSets: false, AutoPopulateAssetSetClassificationTags: [], PrimaryAssetClassID: null })
            : (editAssetClass === null
                ? { ...FacilityStore.getBlankAssetClassUpdateDetail(), Name: newAssetClassName.trim(), SchemaID: targetSchemaId }
                : { ...editAssetClass, Name: newAssetClassName.trim() });

        if (!assetClass) {
            return;
        }

        if (assetClassDialogMode === 'annotationClass') {
            currentSelectedClassNodeTypeRef.current = 'annotationClass';
            await SchemaActions.createOrUpdateAnnotationClass(assetClass);

            const resolvedAnnotationClasses = await fetchAnnotationClassesForSchema(targetSchemaId);
            const savedAnnotationClass = editAssetClass
                ? resolvedAnnotationClasses.find((item) => item.ID === editAssetClass.ID) || resolvedAnnotationClasses.find((item) => item.Name.toLocaleLowerCase() === newAssetClassName.trim().toLocaleLowerCase())
                : resolvedAnnotationClasses.find((item) => item.Name.toLocaleLowerCase() === newAssetClassName.trim().toLocaleLowerCase()) || resolvedAnnotationClasses[0];

            setAssetClasses(resolvedAnnotationClasses);
            expandTreeNodeKey(parentTreeNodeKey);
            remountTreeGrid();

            if (savedAnnotationClass) {
                SchemaActions.setConfigurationData(savedAnnotationClass);
            }

            if (savedAnnotationClass && parentTreeNodeKey) {
                setSelectedTreeNodeKey(`annotation-class-${savedAnnotationClass.ID}-parent-${parentTreeNodeKey}`);
            }
        } else {
            currentSelectedClassNodeTypeRef.current = 'assetClass';
            await SchemaActions.createOrUpdateAssetClass(assetClass);
            const resolvedAssetClasses = await fetchAssetClassesForSchema(targetSchemaId);
            const savedAssetClass = assetClassDialogMode === 'assetClassExtension'
                ? resolvedAssetClasses.find((item) => item.PrimaryAssetClassID === selectedPrimaryAssetClassID)
                : editAssetClass
                    ? resolvedAssetClasses.find((item) => item.ID === editAssetClass.ID) || resolvedAssetClasses.find((item) => item.Name.toLocaleLowerCase() === newAssetClassName.trim().toLocaleLowerCase())
                    : resolvedAssetClasses.find((item) => item.Name.toLocaleLowerCase() === newAssetClassName.trim().toLocaleLowerCase()) || resolvedAssetClasses[0];

            expandTreeNodeKey(parentTreeNodeKey);
            remountTreeGrid();

            if (savedAssetClass && parentTreeNodeKey) {
                setSelectedTreeNodeKey(`asset-class-${savedAssetClass.ID}-parent-${parentTreeNodeKey}`);
            }
        }
    };

    const handleCancelAssetClass = () => {
        setIsAssetClassDialogOpen(false);
        setAssetClassDialogMode('assetClass');
        setEditAssetClass(null);
        setAvailablePrimaryAssetClasses([]);
        setSelectedPrimaryAssetClassID('');
    };

    const treeDataSource = useMemo(() => {
        return sortByName(allSchemas.filter((schema) => !schema.IsDependent)).map((schema) => buildConfigurationTreeNode({
            schema,
            getSchemaExtensions,
            treeAssetClassesBySchema,
            treeAnnotationClassesBySchema,
            collapsedTreeNodeKeys
        }));
    }, [allSchemas, collapsedTreeNodeKeys, getSchemaExtensions, treeAnnotationClassesBySchema, treeAssetClassesBySchema]);


    useEffect(() => {
        const timeoutIds = [0, 40, 120].map((delay) => window.setTimeout(() => {
            paintTreeGridSelection(selectedTreeNodeKey);
        }, delay));

        return () => {
            timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
        };
    }, [selectedTreeNodeKey, treeDataSource]);

    const paintTreeGridSelection = useCallback((treeNodeKey: string | null) => {
        treeGridRef.current?.clearSelection();

        const treeGridElement = document.querySelector<HTMLElement>('.configuration-form-treegrid');
        if (!treeGridElement) {
            return;
        }

        const rows = treeGridElement.querySelectorAll<HTMLTableRowElement>('.e-row');

        rows.forEach((row) => {
            const isSelected = !!treeNodeKey && row.dataset.treeNodeKey === treeNodeKey;
            row.classList.toggle('configuration-form-treegrid-row-selected', isSelected);

            row.querySelectorAll<HTMLTableCellElement>('.e-rowcell').forEach((cell) => {
                cell.classList.toggle('configuration-form-treegrid-cell-selected', isSelected);
            });
        });
    }, []);

    const selectedTreeNode = useMemo(() => findTreeNodeByKey(treeDataSource, selectedTreeNodeKey), [selectedTreeNodeKey, treeDataSource]);
    const externalAttributeDefinitions = configuration?.ExternalAttributeDefinitions || [];
    const externalDataSourceLinks = configuration?.ExternalDataSourceLinks || [];
    const externalAttributeMatchTargets = useMemo(() => {
        const targets = new Map<string, IAttributeDefinition>();

        attributeDefinitions
            .filter((attribute) => attribute.ID && !isNewAttributeID(attribute.ID))
            .forEach((attribute) => targets.set(attribute.ID, attribute));

        if (selectedTreeNode?.NodeType === 'assetClassExtension' && configuration?.PrimaryAssetClassID) {
            const parentSchemaId = getParentSchemaIdFromTreeNodeKey(selectedTreeNode.ParentTreeNodeKey);
            const primaryAssetClass = (treeAssetClassesBySchema[parentSchemaId || ''] || [])
                .find((assetClass) => assetClass.ID === configuration.PrimaryAssetClassID);

            primaryAssetClass?.AttributeDefinitions
                ?.filter((attribute) => attribute.ID && !targets.has(attribute.ID))
                .forEach((attribute) => targets.set(attribute.ID, attribute));
        }

        return Array.from(targets.values()).sort((left, right) => left.Name.localeCompare(right.Name));
    }, [attributeDefinitions, configuration?.PrimaryAssetClassID, selectedTreeNode?.NodeType, selectedTreeNode?.ParentTreeNodeKey, treeAssetClassesBySchema]);
    const hasInvalidExternalAttributeState = isAssetClassNodeType(selectedNodeType)
        && hasInvalidExternalAttributes(externalDataSourceLinks, externalAttributeDefinitions, externalAttributeMatchTargets);
    const canSaveConfiguration = isItemClassNodeType(selectedNodeType)
    ? !!configuration && !hasAutoPopulateClassificationError && !hasInvalidExternalAttributeState
    : !!selectedEditableSchema && !hasInvalidExternalAttributeState;

    const canDeleteSchema = (selectedNodeType === 'schema' || selectedNodeType === 'schemaExtension')
        && !!selectedEditableSchema?.ID
        && PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION)
        && !isActionInProgress;

    const executeDeleteAssetClass = async (assetClassToDelete?: IAssetClassUpdateDetail | null, parentTreeNodeKey?: string | null) => {
        const selectedClass = assetClassToDelete || assetClasses.find((item) => item.ID === configuration?.ID);
        if (!selectedClass) {
            return;
        }

        const nextSelectedTreeNodeKey = parentTreeNodeKey || selectedTreeNode?.ParentTreeNodeKey || null;

        SystemActions.displayConfirmationMessage({
            message: InovxLoader.t(selectedClass.FriendClasses?.length > 0 ? 'facility:message.confirmAssetClassDeletionWithFriendClass' : 'facility:message.confirmAssetClassDeletion'),
            confirmCallback: async () => {
                setActionInProgressMsg(getDeletingProgressMessage());

                try {
                    await SchemaActions.deleteAssetClass(selectedClass);
                    const nextSchemaAssetClasses = (treeAssetClassesBySchemaRef.current[selectedClass.SchemaID] || []).filter((assetClass) => assetClass.ID !== selectedClass.ID);

                    treeAssetClassesBySchemaRef.current = {
                        ...treeAssetClassesBySchemaRef.current,
                        [selectedClass.SchemaID]: nextSchemaAssetClasses
                    };

                    setTreeAssetClassesBySchema((current) => ({
                        ...current,
                        [selectedClass.SchemaID]: nextSchemaAssetClasses
                    }));

                    if ((selectedSubSchema?.ID || selectedSchema?.ID) === selectedClass.SchemaID) {
                        setAssetClasses(nextSchemaAssetClasses);
                    }

                    if (nextSelectedTreeNodeKey) {
                        setSelectedTreeNodeKey(nextSelectedTreeNodeKey);
                    }
                } finally {
                    setActionInProgressMsg(getSavingProgressMessage());
                }
            }
        });
    };

    const executeDeleteAnnotationClass = async (annotationClassToDelete?: IAssetClassUpdateDetail | null, parentTreeNodeKey?: string | null) => {
        const selectedClass = annotationClassToDelete || assetClasses.find((item) => item.ID === configuration?.ID);
        if (!selectedClass) {
            return;
        }

        const nextSelectedTreeNodeKey = parentTreeNodeKey || selectedTreeNode?.ParentTreeNodeKey || null;

        SystemActions.displayConfirmationMessage({
            message: InovxLoader.t('facility:message.confirmAnnotationClassDeletion'),
            confirmCallback: async () => {
                setActionInProgressMsg(getDeletingProgressMessage());

                try {
                    try {
                        await SchemaServices.deleteAnnotationClass(selectedClass);
                    } catch (error) {
                        if (error.handled === false) {
                            const msg = InovxLoader.t('facility:message.annotationClassDeletionFailedBase') + error.message;
                            SystemActions.displayErrorMessage({
                                message: msg
                            });
                            return;
                        }

                        throw error;
                    }

                    const nextSchemaAnnotationClasses = (treeAnnotationClassesBySchemaRef.current[selectedClass.SchemaID] || []).filter((annotationClass) => annotationClass.ID !== selectedClass.ID);

                    treeAnnotationClassesBySchemaRef.current = {
                        ...treeAnnotationClassesBySchemaRef.current,
                        [selectedClass.SchemaID]: nextSchemaAnnotationClasses
                    };

                    setTreeAnnotationClassesBySchema((current) => ({
                        ...current,
                        [selectedClass.SchemaID]: nextSchemaAnnotationClasses
                    }));

                    if ((selectedSubSchema?.ID || selectedSchema?.ID) === selectedClass.SchemaID) {
                        setAssetClasses(nextSchemaAnnotationClasses);
                    }

                    if (nextSelectedTreeNodeKey) {
                        setSelectedTreeNodeKey(nextSelectedTreeNodeKey);
                    }
                } finally {
                    setActionInProgressMsg(getSavingProgressMessage());
                }
            }
        });
    };

    const handleSelectTreeNode = useCallback(async (node: IConfigurationTreeNode, verifyChanges = true, force = false) => {
        if (!node) {
            return false;
        }

        if (!force && node.TreeNodeKey === selectedTreeNodeKey) {
            return false;
        }

        if (verifyChanges && !verifyPendingConfigurationChanges()) {
            return false;
        }

        hideTreeContextMenu();

        if (isItemClassNodeType(node.NodeType)) {
            currentSelectedClassNodeTypeRef.current = node.NodeType === 'annotationClass' ? 'annotationClass' : 'assetClass';
            const schema = getSchemaById(node.SchemaID);
            await applySchemaSelection(getPrimarySchemaForTreeNode(node), schema?.IsDependent ? schema : null, node.AssetClassID || node.ID, node.TreeNodeKey, node.NodeType === 'annotationClass' ? 'annotationClass' : 'assetClass');
            return true;
        }

        const schema = getSchemaById(node.SchemaID);
        const primarySchema = getPrimarySchemaForTreeNode(node) || (node.NodeType === 'schema' ? schema : getPrimarySchemaForSchema(schema));
        const schemaExtension = node.NodeType === 'schemaExtension' ? schema : null;
        await applySchemaSelection(primarySchema, schemaExtension, null, node.TreeNodeKey);
        return true;
    }, [applySchemaSelection, getPrimarySchemaForSchema, getPrimarySchemaForTreeNode, getSchemaById, selectedTreeNodeKey, verifyPendingConfigurationChanges]);

    const schemaUsageList = useMemo(() => {
        const targetSchemaId = selectedSubSchema?.ID || selectedSchema?.ID;
        if (!targetSchemaId) {
            return [];
        }

        return allFacilities
            .filter((facility) => facility.SchemaID === targetSchemaId || facility.Schemas?.includes(targetSchemaId))
            .map((facility) => ({ text: facility.Name, id: facility.ID }));
    }, [allFacilities, selectedSchema, selectedSubSchema]);

    const deleteSchema = () => {
        if (!canDeleteSchema || !selectedEditableSchema || !verifyPendingConfigurationChanges()) {
            return;
        }

        const schemaIDsToDelete = new Set<string>([selectedEditableSchema.ID]);
        let foundDescendants = true;
        while (foundDescendants) {
            const previousSize = schemaIDsToDelete.size;
            allSchemas.forEach((schema) => {
                if (schema.Dependencies?.some((dependencyID) => schemaIDsToDelete.has(dependencyID))) {
                    schemaIDsToDelete.add(schema.ID);
                }
            });
            foundDescendants = schemaIDsToDelete.size !== previousSize;
        }

        const schemaDeletionUsageList = allFacilities.filter((facility) =>
            schemaIDsToDelete.has(facility.SchemaID) || facility.Schemas?.some((schemaID) => schemaIDsToDelete.has(schemaID)));
        if (schemaDeletionUsageList.length > 0) {
            SystemActions.displayErrorMessage({
                message: InovxLoader.t('facility:message.cannotDeleteSchemaInUse')
            });
            return;
        }

        const schemaToDelete = selectedEditableSchema;
        const parentSchema = selectedSubSchema ? selectedSchema : null;
        const nextPrimarySchema = parentSchema || sortByName(allSchemas.filter((schema) => !schema.IsDependent && !schemaIDsToDelete.has(schema.ID)))[0] || null;

        SystemActions.displayConfirmationMessage({
            message: InovxLoader.t('facility:message.confirmSchemaDeletion'),
            confirmCallback: async () => {
                setActionInProgressMsg(getDeletingProgressMessage());

                pendingTreeSelectionRef.current = null;
                if (nextPrimarySchema) {
                    pendingTreeSelectionRef.current = {
                        primarySchemaId: nextPrimarySchema.ID,
                        schemaId: nextPrimarySchema.ID,
                        treeNodeKey: getConfigurationTreeNodeKey(nextPrimarySchema)
                    };
                }

                try {
                    await SchemaActions.deleteSchema(schemaToDelete);

                    const nextAssetClassCache = { ...treeAssetClassesBySchemaRef.current };
                    schemaIDsToDelete.forEach((schemaID) => delete nextAssetClassCache[schemaID]);
                    treeAssetClassesBySchemaRef.current = nextAssetClassCache;
                    setTreeAssetClassesBySchema(nextAssetClassCache);

                    const nextAnnotationClassCache = { ...treeAnnotationClassesBySchemaRef.current };
                    schemaIDsToDelete.forEach((schemaID) => delete nextAnnotationClassCache[schemaID]);
                    treeAnnotationClassesBySchemaRef.current = nextAnnotationClassCache;
                    setTreeAnnotationClassesBySchema(nextAnnotationClassCache);

                    currentSelectedSchemaIdRef.current = nextPrimarySchema?.ID || null;
                    assetSetTaxonomyOriginalRef.current = null;
                    if (!nextPrimarySchema) {
                        SchemaActions.resetAssetSetTaxonomyDetail(null);
                    }
                } catch (error: any) {
                    pendingTreeSelectionRef.current = null;
                    if (error?.handled === false) {
                        SystemActions.displayErrorMessage({
                            message: error.message || InovxLoader.t('facility:message.schemaDeletionFailed')
                        });
                    }
                } finally {
                    setActionInProgressMsg(getSavingProgressMessage());
                }
            }
        });
    };

    const eligibleExternalAttributeFacilities = useMemo(() => {
        const targetSchemaId = selectedSubSchema?.ID || selectedSchema?.ID;
        if (!targetSchemaId) {
            return [];
        }

        return allFacilities
            .filter((facility) => facility.Schemas?.includes(targetSchemaId))
            .sort((left, right) => left.Name.localeCompare(right.Name));
    }, [allFacilities, selectedSchema, selectedSubSchema]);

    const schemaExtensionParentNames = useMemo(() => {
        if (!selectedTreeNode?.Schema?.Dependencies?.length) {
            return [];
        }

        return selectedTreeNode.Schema.Dependencies
            .map((dependencyId) => allSchemas.find((schema) => schema.ID === dependencyId)?.Name)
            .filter(Boolean) as string[];
    }, [allSchemas, selectedTreeNode]);

    const handleTreeRowSelecting = (event: RowSelectingEventArgs) => {
        const target = event.target as Element;
        if (target?.closest('.configuration-form-tree-action-button')) {
            event.cancel = true;
            return;
        }

        if (!verifyPendingConfigurationChanges()) {
            event.cancel = true;
        }
    };

    const handleTreeRowSelected = (event: any) => {
        const selectedNode = (event?.data as IConfigurationTreeNode) || null;

        if (!selectedNode) {
            return;
        }

        [0, 40, 120].forEach((delay) => {
            window.setTimeout(() => {
                paintTreeGridSelection(selectedNode.TreeNodeKey);
            }, delay);
        });

        void handleSelectTreeNode(selectedNode, false);
    };

    const openTreeContextMenu = useCallback((clientX: number, clientY: number, node: IConfigurationTreeNode) => {
        setTreeContextMenu({ visible: true, left: clientX, top: clientY, node });
    }, []);

    const handleTreeRowDataBound = (args: RowDataBoundEventArgs) => {
        const row = args.row as HTMLTableRowElement;
        const node = args.data as IConfigurationTreeNode;

        if (!row || !node) {
            return;
        }

        row.dataset.treeNodeKey = node.TreeNodeKey;
        row.classList.add('cursor-pointer');
    };

    const handleTreeRowExpanded = (event: any) => {
        const node = event?.data as IConfigurationTreeNode | undefined;
        if (!node?.TreeNodeKey) {
            return;
        }

        setCollapsedTreeNodeKeys((current) => {
            if (!current.has(node.TreeNodeKey)) {
                return current;
            }

            const next = new Set(current);
            next.delete(node.TreeNodeKey);
            return next;
        });
    };

    const handleTreeRowCollapsed = (event: any) => {
        const node = event?.data as IConfigurationTreeNode | undefined;
        if (!node?.TreeNodeKey) {
            return;
        }

        setCollapsedTreeNodeKeys((current) => {
            if (current.has(node.TreeNodeKey)) {
                return current;
            }

            const next = new Set(current);
            next.add(node.TreeNodeKey);
            return next;
        });
    };

    const getTreeContextMenuItems = (node: IConfigurationTreeNode) => {
        if (node.NodeType === 'schema') {
            return [
                { id: 'addAssetClass', label: InovxLoader.t('facility:form.addAssetClassTitle') },
                { id: 'addAnnotationClass', label: InovxLoader.t('facility:form.addAnnotationClassTitle') },
                { id: 'addSchemaExtension', label: `${InovxLoader.t('facility:preferences.addButton')} ${InovxLoader.t('facility:form.subSchema')}` }
            ];
        }

        if (node.NodeType === 'schemaExtension') {
            return [
                { id: 'addAssetClass', label: InovxLoader.t('facility:form.addAssetClassTitle') },
                { id: 'addAnnotationClass', label: InovxLoader.t('facility:form.addAnnotationClassTitle') },
                { id: 'addAssetClassExtension', label: InovxLoader.t('facility:form.addAssetClassExtensionTitle') }
            ];
        }

        if (node.NodeType === 'annotationClass') {
            return [
                { id: 'deleteAnnotationClass', label: InovxLoader.t('facility:form.deleteAnnotationClassTitle') }
            ];
        }

        if (node.NodeType === 'assetClassExtension') {
            return [
                { id: 'deleteAssetClassExtension', label: InovxLoader.t('facility:form.deleteAssetClassExtensionTitle') }
            ];
        }

        return [
            { id: 'deleteAssetClass', label: InovxLoader.t('facility:form.deleteAssetClassTitle') }
        ];
    };

    const handleTreeContextMenuItemClick = async (itemId: string) => {
        const activeNode = treeContextMenu.node;
        hideTreeContextMenu();

        if (!activeNode) {
            return;
        }

        if (itemId === 'addSchemaExtension' && activeNode.NodeType === 'schema') {
            if (!verifyPendingConfigurationChanges()) {
                return;
            }

            const schema = getSchemaById(activeNode.SchemaID);
            openAddSchemaDialog(false, schema ? [schema.ID] : []);
            return;
        }

        if (itemId === 'addAssetClass') {
            const didSelect = await handleSelectTreeNode(activeNode, true, true);
            if (didSelect) {
                openAddAssetClassDialog();
            }
            return;
        }

        if (itemId === 'addAnnotationClass') {
            const didSelect = await handleSelectTreeNode(activeNode, true, true);
            if (didSelect) {
                await openAddAnnotationClassDialog();
            }
            return;
        }

        if (itemId === 'addAssetClassExtension' && activeNode.NodeType === 'schemaExtension') {
            const didSelect = await handleSelectTreeNode(activeNode, true, true);
            if (didSelect) {
                await openAddAssetClassExtensionDialog(activeNode);
            }
            return;
        }

        if (itemId === 'editAssetClass' && activeNode.NodeType === 'assetClass') {
            const didSelect = await handleSelectTreeNode(activeNode, true, true);
            if (didSelect) {
                openEditAssetClassDialog(activeNode.AssetClass || null);
            }
            return;
        }

        if (itemId === 'editAnnotationClass' && activeNode.NodeType === 'annotationClass') {
            const didSelect = await handleSelectTreeNode(activeNode, true, true);
            if (didSelect) {
                await openEditAnnotationClassDialog(activeNode.AssetClass || null);
            }
            return;
        }

        if ((itemId === 'deleteAssetClass' || itemId === 'deleteAssetClassExtension') && isAssetClassNodeType(activeNode.NodeType)) {
            const didSelect = await handleSelectTreeNode(activeNode, true, true);
            if (didSelect) {
                await executeDeleteAssetClass(activeNode.AssetClass || null, activeNode.ParentTreeNodeKey || null);
            }
            return;
        }

        if (itemId === 'deleteAnnotationClass' && activeNode.NodeType === 'annotationClass') {
            const didSelect = await handleSelectTreeNode(activeNode, true, true);
            if (didSelect) {
                await executeDeleteAnnotationClass(activeNode.AssetClass || null, activeNode.ParentTreeNodeKey || null);
            }
        }
    };

    const sortedAttributeDefinitions = useMemo(() => [...attributeDefinitions].sort((left, right) => left.Name.localeCompare(right.Name)), [attributeDefinitions]);
    const handleExternalAttributeDefinitionsChange = useCallback((definitions: IExternalAttributeDefinition[]) => {
        ConfigurationActions.editConfigurationProperty(ConfigurationConstants.CONFIGURATION_FORM_EXTERNALATTRIBUTEDATA, 'ExternalAttributeDefinitions', definitions);
    }, []);
    const handleExternalDataSourceLinksChange = useCallback((links: IExternalDataSourceLink[]) => {
        ConfigurationActions.editConfigurationProperty(ConfigurationConstants.CONFIGURATION_FORM_EXTERNALATTRIBUTEDATA, 'ExternalDataSourceLinks', links);
    }, []);
    const sortedFriendClasses = useMemo(() => [...(configuration?.FriendClasses || [])].sort((left, right) => left.Name.localeCompare(right.Name)), [configuration?.FriendClasses]);

    return {
        isActionInProgress,
        actionInProgressMsg,
        hasSchemaChanges: combinedHasSchemaChanges,
        canSaveConfiguration,
        canDeleteSchema,
        configuration,
        selectedTreeNode,
        treeDataSource,
        treeGridRenderKey,
        treeGridRef,
        treeContextMenu,
        schemaUsageList,
        schemaExtensionParentNames,
        schemaDetailsSectionProps: {
            editableSchemaName: schemaDetailsForm.schemaName,
            hasSchemaNameError: schemaDetailsErrors.schemaName,
            onSchemaNameChange: handleSchemaDetailsSchemaNameChange,
            assetSetTaxonomyDetail,
            onAssetSetTaxonomyChange: handleAssetSetTaxonomyChange,
            assetSetTaxonomyResetToken
        },
        assetClassDetailsSectionProps: {
            editableAssetClassName: schemaDetailsForm.assetClassName,
            hasAssetClassNameError: schemaDetailsErrors.assetClassName,
            onAssetClassNameChange: handleSchemaDetailsAssetClassNameChange
        },
        selectedAssetClassTab,
        saveConfiguration,
        deleteSchema,
        cancel,
        onAddSchema,
        setSelectedAssetClassTab,
        handleTreeRowSelecting,
        handleTreeRowSelected,
        handleTreeRowDataBound,
        handleTreeRowExpanded,
        handleTreeRowCollapsed,
        hideTreeContextMenu,
        openTreeContextMenu,
        getTreeContextMenuItems,
        handleTreeContextMenuItemClick,
        formFieldsSectionProps: {
            sortedAttributeDefinitions,
            attributeDataTypeLabels,
            onAddAttribute,
            onEditAttribute,
            onDeleteAttribute: handleDeleteAttribute
        },
        externalAttributesSectionProps: {
            assetClassID: configuration?.ID || '',
            links: externalDataSourceLinks,
            definitions: externalAttributeDefinitions,
            matchTargetAttributes: externalAttributeMatchTargets,
            facilities: eligibleExternalAttributeFacilities,
            onLinksChange: handleExternalDataSourceLinksChange,
            onDefinitionsChange: handleExternalAttributeDefinitionsChange
        },
        validationSettingsSectionProps: {
            configuration: configuration as IAssetClassUpdateDetail,
            tagShapeOptions: getTagShapeOptions(),
            fontSizeOptions,
            fontStyleOptions: getFontStyleOptions(),
            onSelectChanged,
            onChangeColor: changeColor
        },
        importMappingSectionProps: {
            configurationName: configuration?.Name || '',
            sortedFriendClasses,
            onAddFriendClass,
            onEditFriendClass,
            onDeleteFriendClass: handleDeleteFriendClass,
            assetSetTaxonomyDetail,
            autoPopulateAssetSets: !!configuration?.AutoPopulateAssetSets,
            selectedAssetSetClassificationTags: configuration?.AutoPopulateAssetSetClassificationTags || [],
            hasAutoPopulateClassificationError,
            onAutoPopulateAssetSetsChange: handleAutoPopulateAssetSetsChange,
            onAssetSetClassificationTagsChange: handleAssetSetClassificationTagsChange
        },
        dialogsProps: {
            attributeDialog: {
                isOpen: isAddAttributeDialogOpen,
                editAttribute,
                hasAddAttributeError,
                newAttributeName,
                newAttributeDataType,
                attributeDataTypeLabels,
                isNewAttributeId: isNewAttributeID,
                onInputChange: handleAddAttributeInputChange,
                onDataTypeChange: setNewAttributeDataType,
                onSave: handleSaveNewAttribute,
                onCancel: handleCancelAddAttribute
            },
            schemaDialog: {
                isOpen: isAddSchemaDialogOpen,
                isSchemaPrimary,
                newSchemaName,
                dependencies,
                schemaFormError,
                primarySchemas,
                onInputChange: handleSchemaInputChange,
                onPrimarySchemaChange,
                onSave: handleSaveSchema,
                onCancel: handleCancelSchema,
                onBeforeClose: handleBeforeClose,
                getSchemaById
            },
            assetClassDialog: {
                isOpen: isAssetClassDialogOpen,
                mode: assetClassDialogMode,
                editAssetClass,
                hasAssetClassError,
                newAssetClassName,
                availablePrimaryAssetClasses,
                selectedPrimaryAssetClassID,
                assetClassInputRef,
                onInputChange: handleAssetClassInputChange,
                onPrimaryAssetClassChange: setSelectedPrimaryAssetClassID,
                onSave: handleSaveAssetClass,
                onCancel: handleCancelAssetClass
            },
            friendClassDialog: {
                isOpen: isFriendClassDialogOpen,
                editFriendClass,
                hasFriendClassError,
                newFriendClassName,
                onInputChange: handleFriendClassInputChange,
                onSave: handleSaveFriendClass,
                onCancel: handleCancelFriendClass
            }
        }
    };
};

export default useConfigurationForm;
